import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation, type TFunction } from 'react-i18next';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import {
  hasBuiltGeometry,
  isGeometryMisconfigured,
  type MapEditorCatalogElement,
} from '../mapEditorCatalog';
import { moduleLengthMetres } from '../mapEditorPlan';
import { describeSpotCount } from '../mapEditorSpotText';
import {
  buildRootTypes,
  findRootFor,
  searchRootTypes,
  type MapEditorRootKind,
  type MapEditorRootType,
  type MapEditorVariant,
} from '../mapEditorRoots';
import { ModulePlanGlyph, RoomPicture } from './PlanGlyph';
import {
  segmentPickerPaperSx,
  useSegmentPickerStyles,
  VARIANT_CARD_STEP_PX,
} from './styles';

// What the picker was opened for. It decides three things — the title, which tabs are offered, and
// what the commit button says — and the middle one is a correctness rule rather than a preference:
// UE refuses a Start element anywhere but at index 0 and reports it as `start-not-first`, a refusal
// that names a fault in the sender. So the Lobbies tab exists only for the entrance row.
export type SegmentPickerPurpose = 'append' | 'replace' | 'entrance';

// How many variant cards fit the track before it has to scroll. The corridors ship six, which is the
// most any root has, so the arrows appear for exactly them.
const VARIANTS_BEFORE_SCROLL = 5;

interface ISegmentPickerProps {
  open: boolean;
  purpose: SegmentPickerPurpose;
  // The whole catalog. Grouped into roots here rather than by the caller, so the grouping happens
  // once and the seeding below can look the current module up in the same list it will offer.
  elements: MapEditorCatalogElement[];
  // What is standing at the row being changed, so **Change** opens on that module's own card with its
  // own variant selected rather than at the top of the kit. Empty for Add, and for an entrance the
  // author has not chosen yet.
  seedElementId?: string;
  // Offered in the entrance picker as a way back to the entrance the host map bakes in. Undoing a
  // choice is the one thing the picker cannot express by picking something, so it is a control of its
  // own — and losing it would strand a layout on whichever lobby it was last given.
  onUseMapEntrance?: () => void;
  onClose: () => void;
  // Called with the chosen element, then the picker closes. Staying open after a commit was the thing
  // that read as "did that actually work?" — so committing and closing is one action, not two.
  onCommit: (elementId: string) => void;
}

const TAB_KINDS: {
  kind: MapEditorRootKind;
  labelKey: string;
  labelFallback: string;
}[] = [
  {
    kind: 'room',
    labelKey: 'editorMode.mapEditorPickerTabRooms',
    labelFallback: 'Rooms',
  },
  {
    kind: 'corridor',
    labelKey: 'editorMode.mapEditorPickerTabCorridors',
    labelFallback: 'Corridors',
  },
  {
    kind: 'entrance',
    labelKey: 'editorMode.mapEditorPickerTabLobbies',
    labelFallback: 'Lobbies',
  },
];

/**
 * Names one variant: whichever of size and direction actually tells it apart from its siblings.
 *
 * The size words follow the family, because the kit's own names do: a corridor comes Short, Medium and
 * Long, and a room Small, Medium and Large. Calling a corridor "Small" would be this hub's word rather
 * than the kit's, in a control sitting directly under the kit's own name for it.
 *
 * @param variant - The variant being named.
 * @param kind - Which tab it is under, which decides the size vocabulary.
 * @param translate - i18next `t`.
 * @returns The label, or the module's own display name when nothing distinguishes it.
 */
const describeVariant = (
  variant: MapEditorVariant,
  kind: MapEditorRootKind,
  translate: TFunction,
): string => {
  const sizes: Record<string, { key: string; fallback: string }> =
    kind === 'corridor'
      ? {
          S: { key: 'editorMode.mapEditorLengthS', fallback: 'Short' },
          M: { key: 'editorMode.mapEditorLengthM', fallback: 'Medium' },
          L: { key: 'editorMode.mapEditorLengthL', fallback: 'Long' },
        }
      : {
          S: { key: 'editorMode.mapEditorSizeS', fallback: 'Small' },
          M: { key: 'editorMode.mapEditorSizeM', fallback: 'Medium' },
          L: { key: 'editorMode.mapEditorSizeL', fallback: 'Large' },
        };

  const size = variant.sizeKey ? sizes[variant.sizeKey] : undefined;
  const sizeLabel = size ? translate(size.key, size.fallback) : variant.sizeKey;

  // Two vocabularies for one field, and the difference is what the label is doing. Alone it is the
  // whole answer and reads as a sentence — a Rotunda "Turns left". Beside a size it is the second half
  // of a compound and reads as a word — "Short · left".
  const directions: Record<string, { key: string; fallback: string }> =
    sizeLabel
      ? {
          straight: {
            key: 'editorMode.mapEditorDirectionStraightShort',
            fallback: 'straight',
          },
          left: {
            key: 'editorMode.mapEditorDirectionLeftShort',
            fallback: 'left',
          },
          right: {
            key: 'editorMode.mapEditorDirectionRightShort',
            fallback: 'right',
          },
        }
      : {
          straight: {
            key: 'editorMode.mapEditorDirectionStraight',
            fallback: 'Straight',
          },
          left: {
            key: 'editorMode.mapEditorDirectionLeft',
            fallback: 'Turns left',
          },
          right: {
            key: 'editorMode.mapEditorDirectionRight',
            fallback: 'Turns right',
          },
        };

  const direction = variant.directionKey
    ? directions[variant.directionKey]
    : undefined;
  const directionLabel = direction
    ? translate(direction.key, direction.fallback)
    : variant.directionKey;

  const parts = [sizeLabel, directionLabel].filter((part) => !!part);

  return parts.length ? parts.join(' · ') : variant.fallbackLabel;
};

/**
 * The line under the chosen module's name: how long it is, and what is in it.
 *
 * @param element - The module.
 * @param translate - i18next `t`.
 * @returns The line, with whichever halves this build can answer for.
 */
const describeModule = (
  element: MapEditorCatalogElement,
  translate: TFunction,
): string => {
  const metres = moduleLengthMetres(element);
  const parts = [
    metres === null
      ? ''
      : translate('editorMode.mapEditorLengthMetres', '{{metres}} m long', {
          metres,
        }),
    describeSpotCount(element, translate),
  ].filter((part) => !!part);

  return parts.join(' · ');
};

/**
 * The picker: root types on the left, the chosen one's picture and its variants on the right.
 *
 * Replaces a facet tree in the Scene panel and the wall-layout popup that tree opened. Both are gone
 * deliberately — see mapEditorRoots for why the facets stopped identifying a corridor, and do not
 * re-add a size or direction filter row: they are variants now, which is what makes the carousel
 * worth having and what stops a room being asked which way it turns.
 *
 * @param props - See ISegmentPickerProps.
 */
export const SegmentPicker = ({
  open,
  purpose,
  elements,
  seedElementId = '',
  onUseMapEntrance,
  onClose,
  onCommit,
}: ISegmentPickerProps) => {
  const { t: translate } = useTranslation();
  const styles = useSegmentPickerStyles();

  const roots = useMemo(() => buildRootTypes(elements), [elements]);

  // The tabs this purpose may offer. An entrance may be any room in the kit, so it gets all three;
  // a link in the chain may not be a purpose-built shell, so it gets two. Not a matter of taste:
  // UE refuses a Start element anywhere but at index 0, and reports it as a fault in the sender —
  // an author who picked one from a grid would be told about a bug they cannot act on.
  const kinds: MapEditorRootKind[] =
    purpose === 'entrance'
      ? ['room', 'corridor', 'entrance']
      : ['room', 'corridor'];

  const [tab, setTab] = useState<MapEditorRootKind>('room');
  const [rootId, setRootId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [query, setQuery] = useState('');

  const trackRef = useRef<HTMLDivElement | null>(null);

  // Seeded on every open rather than once on mount, because the dialog is kept mounted and reopened
  // for a different row each time: **Change** on the third segment and then on the fifth are two
  // different questions, and the second one has to open on the fifth segment's own module.
  useEffect(() => {
    if (!open) {
      return;
    }

    setQuery('');

    const seeded = seedElementId
      ? findRootFor(roots, seedElementId)
      : undefined;
    if (seeded && kinds.includes(seeded.kind)) {
      setTab(seeded.kind);
      setRootId(seeded.id);
      setVariantId(seedElementId);
      return;
    }

    // Nothing to seed from. The entrance opens on the Lobbies tab, since the shells are the rooms
    // made for it; everything else opens on Rooms.
    const openingTab: MapEditorRootKind =
      purpose === 'entrance' ? 'entrance' : 'room';
    const first =
      roots.find((root) => root.kind === openingTab) ??
      roots.find((root) => kinds.includes(root.kind));

    setTab(first ? first.kind : openingTab);
    setRootId(first ? first.id : '');
    setVariantId(first ? first.variants[0].element.id : '');
    // `kinds` is derived from `purpose` and rebuilt every render, so it is deliberately not a
    // dependency — it would re-seed the dialog on every keystroke in the search box.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedElementId, purpose, roots]);

  // What the left column lists. A search covers every tab this purpose may offer rather than only the
  // active one — an author typing "lit" is looking for the Lit corridors and should not have to know
  // they are filed under Corridors first — but it can never reach past the purpose, or Add would offer
  // a lobby the chain cannot carry.
  const listed = useMemo(() => {
    const allowed = roots.filter((root) => kinds.includes(root.kind));

    return query.trim()
      ? searchRootTypes(allowed, query)
      : allowed.filter((root) => root.kind === tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots, query, tab, purpose]);

  const activeRoot: MapEditorRootType | undefined =
    roots.find((root) => root.id === rootId) ?? listed[0];
  const activeVariant: MapEditorVariant | undefined =
    activeRoot?.variants.find((variant) => variant.element.id === variantId) ??
    activeRoot?.variants[0];
  const chosen = activeVariant?.element;

  const handlePickTab = useCallback((kind: MapEditorRootKind) => {
    setTab(kind);
    // The search is cleared with the tab: it searched across tabs, so leaving it standing would show
    // a list that has nothing to do with the tab now highlighted.
    setQuery('');
  }, []);

  const handlePickRoot = useCallback((root: MapEditorRootType) => {
    setRootId(root.id);
    setVariantId(root.variants[0].element.id);
    // Back to the first card, so the carousel is not left scrolled to where the previous root's sixth
    // variant used to be.
    if (trackRef.current) {
      trackRef.current.scrollLeft = 0;
    }
  }, []);

  const handleScrollVariants = useCallback((direction: -1 | 1) => {
    trackRef.current?.scrollBy({
      left: direction * VARIANT_CARD_STEP_PX,
      behavior: 'smooth',
    });
  }, []);

  const handleCommit = useCallback(() => {
    if (!chosen) {
      return;
    }
    onCommit(chosen.id);
    onClose();
  }, [chosen, onCommit, onClose]);

  const title = {
    append: translate('editorMode.mapEditorPickerAdd', 'Add a segment'),
    replace: translate(
      'editorMode.mapEditorPickerChange',
      'Change this segment',
    ),
    entrance: translate(
      'editorMode.mapEditorPickerEntrance',
      'Choose the entrance',
    ),
  }[purpose];

  const commitLabel =
    purpose === 'append'
      ? translate('editorMode.mapEditorPickerCommitAdd', 'Add')
      : translate('editorMode.mapEditorPickerCommitUse', 'Use this one');

  // Which question the carousel is asking. A corridor's variants are the same corridor at four
  // lengths and two turns, and calling that "which size" would be asking about half of it.
  const variantsLabel =
    activeRoot?.kind === 'corridor'
      ? translate('editorMode.mapEditorPickerWhichCorridor', 'Which corridor')
      : translate('editorMode.mapEditorPickerWhichSize', 'Which size');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // The id rides the paper, not the Dialog root. The root is MUI's positioning wrapper, and a
      // spec waiting on it for `visible` is the debugging session nobody wants — the element exists
      // and its children are on screen, so the failure reads as "the dialog never opened". The
      // dialog this replaced put it here too; see the MUI slot table in CLAUDE.md.
      PaperProps={
        {
          sx: segmentPickerPaperSx,
          'data-testid': 'map-editor-segment-picker',
        } as never
      }
      maxWidth={false}
    >
      <Box sx={styles.header}>
        <Typography sx={styles.headerTitle}>{title}</Typography>

        <Box sx={styles.tabs}>
          {TAB_KINDS.filter((entry) => kinds.includes(entry.kind)).map(
            (entry) => (
              <Box
                key={entry.kind}
                sx={[
                  styles.tab,
                  tab === entry.kind && !query.trim() && styles.tabActive,
                ]}
                onClick={() => handlePickTab(entry.kind)}
                data-testid={`map-editor-picker-tab-${entry.kind}`}
              >
                {translate(entry.labelKey, entry.labelFallback)}
              </Box>
            ),
          )}
        </Box>

        <Box sx={styles.headerSpacer} />

        <Box sx={styles.search}>
          <SearchOutlinedIcon sx={styles.searchIcon} />
          <Box
            component="input"
            type="text"
            value={query}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setQuery(event.target.value)
            }
            placeholder={translate(
              'editorMode.mapEditorPickerSearch',
              'Search the kit',
            )}
            sx={styles.searchInput}
            data-testid="map-editor-picker-search"
          />
        </Box>

        <IconButton size="small" onClick={onClose} sx={styles.closeButton}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={styles.body}>
        <Box sx={styles.rootsColumn}>
          {listed.map((root) => {
            // Both greybox cases, read off the root's first variant: the variants of a root are one
            // module at different lengths, and a kit does not ship the art for half of them.
            const [first] = root.variants;
            const isMisconfigured = isGeometryMisconfigured(first.element);
            const isUnbuilt = !hasBuiltGeometry(first.element);

            return (
              <Box
                key={root.id}
                sx={[
                  styles.rootCard,
                  root.id === activeRoot?.id && styles.rootCardActive,
                ]}
                onClick={() => handlePickRoot(root)}
                data-testid={`map-editor-picker-root-${root.slug}`}
              >
                <Box sx={styles.rootGlyph}>
                  <ModulePlanGlyph
                    element={first.element}
                    width={48}
                    height={36}
                  />
                </Box>
                <Box sx={styles.rootText}>
                  <Typography sx={styles.rootName}>{root.name}</Typography>
                  <Typography sx={styles.rootHint}>
                    {root.variants.length > 1
                      ? translate(
                          'editorMode.mapEditorPickerVariantCount',
                          '{{count}} variants',
                          { count: root.variants.length },
                        )
                      : root.description || root.category}
                  </Typography>
                </Box>
                {(isMisconfigured || isUnbuilt) && (
                  <Tooltip
                    title={
                      isMisconfigured
                        ? translate(
                            'editorMode.mapEditorGeometryMisconfigured',
                            'This build cannot load the art for it — you will get a grey box.',
                          )
                        : translate(
                            'editorMode.mapEditorGeometryUnbuilt',
                            'No art built for it yet — you will get a grey box.',
                          )
                    }
                  >
                    <WarningAmberOutlinedIcon sx={styles.rootWarning} />
                  </Tooltip>
                )}
              </Box>
            );
          })}

          {/* Two empty states, because there are two reasons to be empty and only one of them is the
              author's doing. A search that matched nothing is theirs to undo, and the way out is
              offered. A tab that is empty because the kit ships nothing of that kind — no lobby among
              its `role: "start"` elements, no corridors at all — is a fact about the build, and
              blaming it on a search box the author never typed in, then offering to clear a search
              that is already empty, is a control that cannot do what it says. */}
          {!listed.length &&
            (query.trim() ? (
              <Box sx={styles.noRoots}>
                <Typography sx={styles.noRootsText}>
                  {translate(
                    'editorMode.mapEditorPickerNoMatch',
                    'Nothing in the kit matches that.',
                  )}
                </Typography>
                <Typography
                  sx={styles.noRootsAction}
                  onClick={() => setQuery('')}
                  data-testid="map-editor-picker-clear-search"
                >
                  {translate(
                    'editorMode.mapEditorPickerClearSearch',
                    'Clear the search',
                  )}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={styles.noRoots}
                data-testid="map-editor-picker-empty-tab"
              >
                <Typography sx={styles.noRootsText}>
                  {translate(
                    'editorMode.mapEditorPickerEmptyTab',
                    "This build's kit ships nothing of this kind.",
                  )}
                </Typography>
              </Box>
            ))}
        </Box>

        <Box sx={styles.pickColumn}>
          {chosen && <RoomPicture element={chosen} showDrawingNote />}

          {activeRoot && (
            <Box>
              <Typography sx={styles.variantsLabel}>{variantsLabel}</Typography>
              <Box sx={styles.variantsRow}>
                {activeRoot.variants.length > VARIANTS_BEFORE_SCROLL && (
                  <IconButton
                    size="small"
                    sx={styles.variantArrow}
                    onClick={() => handleScrollVariants(-1)}
                  >
                    <ChevronLeftOutlinedIcon fontSize="small" />
                  </IconButton>
                )}

                <Box sx={styles.variantsTrack} ref={trackRef}>
                  {activeRoot.variants.map((variant) => (
                    <Box
                      key={variant.element.id}
                      sx={[
                        styles.variantCard,
                        variant.element.id === chosen?.id &&
                          styles.variantCardActive,
                      ]}
                      onClick={() => setVariantId(variant.element.id)}
                      data-testid={`map-editor-picker-variant-${variant.element.id}`}
                    >
                      <Box sx={styles.variantGlyph}>
                        <ModulePlanGlyph
                          element={variant.element}
                          width={84}
                          height={40}
                        />
                      </Box>
                      <Box>
                        <Typography sx={styles.variantLabel}>
                          {describeVariant(variant, activeRoot.kind, translate)}
                        </Typography>
                        <Typography sx={styles.variantHint}>
                          {describeSpotCount(variant.element, translate)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {activeRoot.variants.length > VARIANTS_BEFORE_SCROLL && (
                  <IconButton
                    size="small"
                    sx={styles.variantArrow}
                    onClick={() => handleScrollVariants(1)}
                  >
                    <ChevronRightOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <Box sx={styles.footer}>
        <Box sx={styles.footerText}>
          <Typography sx={styles.footerName}>
            {chosen ? chosen.displayName : ''}
          </Typography>
          <Typography sx={styles.footerMeta}>
            {chosen ? describeModule(chosen, translate) : ''}
          </Typography>
        </Box>

        {purpose === 'entrance' && onUseMapEntrance && (
          <Button
            sx={styles.secondaryButton}
            onClick={() => {
              onUseMapEntrance();
              onClose();
            }}
            data-testid="map-editor-picker-map-entrance"
          >
            {translate(
              'editorMode.mapEditorPickerMapEntrance',
              "Use the map's own entrance",
            )}
          </Button>
        )}

        <Button
          sx={styles.commitButton}
          onClick={handleCommit}
          disabled={!chosen}
          startIcon={
            purpose === 'append' ? <AddOutlinedIcon /> : <CheckOutlinedIcon />
          }
          data-testid="map-editor-picker-commit"
        >
          {commitLabel}
        </Button>
      </Box>
    </Dialog>
  );
};

export default SegmentPicker;
